/**
 * Preauthorization Submission Dialog
 * For submitting PoC for preauthorization (pending -> submitted)
 */

import React, { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  IconButton,
  CircularProgress,
  Stack,
  alpha,
} from '@mui/material'
import {
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  CalendarToday as CalendarIcon,
} from '@mui/icons-material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { format } from 'date-fns'

interface PreauthSubmissionDialogProps {
  open: boolean
  onClose: () => void
  onSubmit: (submitDate: string) => Promise<void>
  loading?: boolean
}

const PreauthSubmissionDialog: React.FC<PreauthSubmissionDialogProps> = ({
  open,
  onClose,
  onSubmit,
  loading = false,
}) => {
  const [submitDate, setSubmitDate] = useState<Date | null>(new Date())

  const handleSubmit = async () => {
    if (!submitDate) {
      return
    }
    // Convert date to ISO string format
    const isoDate = submitDate.toISOString()
    await onSubmit(isoDate)
  }

  const handleClose = () => {
    if (!loading) {
      setSubmitDate(new Date())
      onClose()
    }
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 12px 48px rgba(0, 0, 0, 0.15)',
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pb: 2.5,
            pt: 3,
            px: 3,
            background: 'linear-gradient(135deg, #2E7DAF 0%, #2563a0 100%)',
            color: 'white',
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                p: 1,
                borderRadius: 1.5,
                backgroundColor: alpha('#fff', 0.2),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CalendarIcon sx={{ fontSize: 24 }} />
            </Box>
            <Typography variant="h6" fontWeight={600}>
              Submit for Preauthorization
            </Typography>
          </Stack>
          <IconButton
            onClick={handleClose}
            size="small"
            disabled={loading}
            sx={{
              color: 'white',
              '&:hover': {
                backgroundColor: alpha('#fff', 0.1),
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ py: '2rem !important', px: '2.5rem !important' }}>
          <Stack spacing={3}>
            <Box>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 2, fontWeight: 500 }}
              >
                Select the submission date for preauthorization
              </Typography>
              <DatePicker
                label="Submission Date"
                value={submitDate}
                onChange={newValue => {
                  // Convert PickerValue (Date | Dayjs) to Date
                  const dateObj = newValue instanceof Date ? newValue : (newValue as any)?.toDate ? (newValue as any).toDate() : (newValue ? new Date(newValue as any) : null)
                  setSubmitDate(dateObj)
                }}
                disabled={loading}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    variant: 'outlined',
                    sx: {
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        '&:hover fieldset': {
                          borderColor: '#2E7DAF',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#2E7DAF',
                          borderWidth: 2,
                        },
                      },
                    },
                  },
                }}
              />
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2.5,
            borderTop: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'grey.50',
          }}
        >
          <Button
            onClick={handleClose}
            disabled={loading}
            sx={{
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: alpha('#000', 0.04),
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={loading || !submitDate}
            variant="contained"
            startIcon={
              loading ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <CheckCircleIcon />
              )
            }
            sx={{
              backgroundColor: '#2E7DAF',
              borderRadius: 2,
              px: 3,
              py: 1,
              fontWeight: 600,
              textTransform: 'none',
              boxShadow: '0 4px 12px rgba(46, 125, 175, 0.3)',
              '&:hover': {
                backgroundColor: '#2563a0',
                boxShadow: '0 6px 16px rgba(46, 125, 175, 0.4)',
              },
              '&:disabled': {
                backgroundColor: alpha('#2E7DAF', 0.5),
              },
            }}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  )
}

export default PreauthSubmissionDialog
